import { clearModals } from '@ncr-retail/base-retail-components';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useAuthContext } from 'src/auth/auth-context';
import { IdmPermissionDenied, SessionExpired } from 'src/common/helpers/error-code';
import { ForceSignInDto } from 'src/common/interfaces/dtos/IForceSignIn';
import { showDeviceAlertDialog } from 'src/device-status/device-status';
import { needToForceSignOff } from 'src/login-form/iam-helper';
import { useFastLogin } from 'src/login/hooks/useFastLogin';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { useStyles } from 'src/touchpoint/components/user-login/user-login-styles';
import {
  getMetaData,
  useIsEmeraldSellingService,
} from 'src/xlr-configuration/pos-service-type-utils';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { RedirectContext, XLRStartSequenceState } from 'src/xlr-configuration/xlr-startup';
import DynamicDialogue from '../../common/components/interactive-dialogue/components/dynamic-dialogue/dynamic-dialogue';
import ForceSignoffDialogue from '../../common/components/interactive-dialogue/components/force-signoff-dialogue/force-signoff-dialogue';
import InteractiveDialogueContainer from '../../common/components/interactive-dialogue/components/interactive-dialogue-container';
import { EmeraldDialogTypes } from '../../common/enumerations/emerald-dialog-types';
import { userLockStatus } from '../../common/enumerations/user-lock-status';
import { LocalStorage } from '../../common/helpers/local-storage';
import XLRLogger from '../../common/helpers/logger';
import { ILoginUser } from '../../common/interfaces/dtos/ILoginUser';
import { ILoginUserErrors } from '../../common/interfaces/dtos/ILoginUserErrors';
import { AppState, store } from '../../common/redux/configure-store';
import { LoginActions } from '../../common/redux/reducers/login-reducer';
import { MenuPanelActions } from '../../common/redux/reducers/menu-panel-reducer';
import { ScannedDataHandler, updateScannerCallback } from '../../devices/scanner-operations';
import { useEndOfDayStatus } from '../../end-of-day/hooks/end-of-day-hook';
import UserLogin from '../../touchpoint/components/user-login/user-login';
import { authorizeUser } from '../business-logic/authorize-user';
import { forceSignIn } from '../business-logic/force-sign-in';
import { forceSignOff } from '../business-logic/force-sign-off';
import { posLogin } from '../business-logic/pos-sign-in';
import { invalidLoginTlog } from '../business-logic/service/invalid-login-tlog';
import { unlockPos } from '../business-logic/unlock-pos';

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;
const LoginWrapper: React.FC<Props> = (props: Props): JSX.Element => {
  const { callTlogInvalidPassword } = getCurrentXLRConfig();
  const { useTrainingModeAPIs } = getCurrentXLRConfig();
  const { t } = useTranslation();
  const [user, setUser] = useState<ILoginUser>({});
  const [supervisor, setSupervisor] = useState<ILoginUser>({});
  const [errors, setErrors] = useState<ILoginUserErrors>({});
  const [loginDate, setLoginDate] = useState<string>('');
  const [loginTime, setLoginTime] = useState<string>('');

  const [lockLabel, setLockLabel] = useState<string>('');
  const [submitLabel, setSubmitLabel] = useState<string>('SignInView_SignIn');
  const [alternateBtnLabel, setAlternateBtnLabel] = useState<string>('SignInView_Cancel');
  const [message, setMessage] = useState<string>('');

  const [lockUserStatus, setUserLockStatus] = useState<userLockStatus>(userLockStatus.None);
  const [isAlternateButtonDisabled, setIsAlternateButtonDisabled] = useState<boolean>(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
  const [forceSignOffPopUp, setForceSignOffPopUp] = useState<boolean>(false);
  // use useRef to separate local props out from useEffect/useCallback dependencies
  const refUser = useRef<ILoginUser>({});
  const refErrors = useRef<ILoginUserErrors>();
  const refLockUserStatus = useRef<userLockStatus>();
  const waitForDevices = useRef<boolean>();
  const devicesReady = useRef<boolean>();

  const { setStartupState } = useContext(RedirectContext);
  const { logout, getUser } = useAuthContext();

  const isEmerald = useIsEmeraldSellingService();
  const classes = useStyles();

  // Clear this every time this component mounts
  useEffect(() => {
    devicesReady.current = true;
  }, []);

  useEndOfDayStatus(props.transactionExists);
  useEffect((): void => {
    refUser.current = { ...user };
  }, [user]);

  useEffect((): void => {
    refErrors.current = { ...errors };
  }, [errors]);

  useEffect((): void => {
    refLockUserStatus.current = lockUserStatus;
  }, [lockUserStatus]);

  const setLockedLogin = useCallback((lockoutUser: string): void => {
    setUser(
      (prevUser: ILoginUser): ILoginUser => ({
        ...prevUser,
        userName: lockoutUser,
        lockout: true,
      }),
    );
    store.dispatch(LoginActions.updateUserToken(LocalStorage.getLockUserToken()));
    setLabels('Unlock', 'Lock_ForceSignOff', 'LockedByUser');
  }, []);

  const setNormalLogin = useCallback((): void => {
    setUser({});
    setLabels('SignInView_SignIn', 'SignInView_Cancel');
  }, []);

  useEffect((): void => {
    if (LocalStorage.computerName()) {
      const lockoutUser = LocalStorage.getLockUser();
      if (lockoutUser) {
        setLockedLogin(lockoutUser);
      } else {
        setNormalLogin();
      }
    } else {
      if (!isEmerald) {
        setStartupState(XLRStartSequenceState.Setup);
      }
    }
  }, [setLockedLogin, setNormalLogin, setStartupState, isEmerald]);

  const setLabels = (submitLabel: string, cancelLabel: string, lockLabel?: string): void => {
    setLockLabel(lockLabel ?? '');
    setAlternateBtnLabel(cancelLabel);
    setSubmitLabel(submitLabel);
  };

  useEffect((): void => {
    const lockedUser = LocalStorage.getLockUser();
    const enteredUserName = user.userName;

    if (lockedUser) {
      if (enteredUserName) {
        //user is locked and the user entered something
        checkForLockScenario(
          lockedUser,
          enteredUserName,
          setUserLockStatus,
          setIsAlternateButtonDisabled,
          setIsSubmitDisabled,
        );
      } else {
        //user is locked and nothing is entered yet, or the user cleared the inputs
        setIsSubmitDisabled(true);
        setIsAlternateButtonDisabled(true);
      }
    } else {
      //Normal login scenario
      setIsSubmitDisabled(false);
      setIsAlternateButtonDisabled(false);
    }
  }, [isEmerald, user.userName]);

  const setErrorMessage = useCallback(
    (err: any = { code: '' }): void => {
      const errorMessage = t(err.code);

      setErrors(
        (prevErr: ILoginUserErrors): ILoginUserErrors => ({
          ...prevErr,
          Description: errorMessage,
        }),
      );
    },
    [t],
  );

  const loginProcess = useCallback(
    async (loginUser: ILoginUser): Promise<void> => {
      await posLogin(loginUser, useTrainingModeAPIs)
        .then((): void => {
          XLRLogger.debug({
            message: `${loginUser.userName} logged in`,
            source: 'login-wrapper.tsx',
          });
          setStartupState(XLRStartSequenceState.PostLogin);
        })
        .catch((err): void => {
          if (err.code === 'UserAlreadySignedinInStore') {
            setMessage(err.message);
            setUserLockStatus(userLockStatus.ConfirmSupervisor);
          } else {
            if (err.code === 'Unauthorized') {
              if (callTlogInvalidPassword) {
                invalidLoginTlog(loginUser);
              }
              err.code = 'IdmInvalidUserNamePasswordLoginProvided';
            }
            setErrorMessage(err);
            resetUserLogin();
          }
        });
    },
    [setStartupState, setErrorMessage, callTlogInvalidPassword, useTrainingModeAPIs],
  );

  const forceSignOffCallback = useCallback(
    async (loginUser: ILoginUser): Promise<void> => {
      const { workerId } = LocalStorage.getLockUserObject();
      const loginParameter = { ...loginUser, workerId };
      const { EDGE_IAM } = getMetaData().xlrConfig;
      await forceSignOff(loginParameter, props.trainingMode, logout)
        .then((): void => {
          setErrorMessage();
          unlockPos();
          setNormalLogin();
          setUserLockStatus(userLockStatus.None);
        })
        .catch((err): void => {
          if (EDGE_IAM) {
            popupMessage({
              message: 'Lock_ForceSignOff',
              subMessage: 'InsufficentPrivileges',
            });
          } else {
            setErrorMessage(err);
            resetUserLogin(true);
          }
        });
    },
    [logout, setNormalLogin, props.trainingMode, setErrorMessage],
  );

  const unlockPOS = useCallback(
    async (loginUser: ILoginUser): Promise<void> => {
      await authorizeUser(loginUser, useTrainingModeAPIs)
        .then((): void => {
          unlockPos(true);
          setStartupState(XLRStartSequenceState.PostLogin);
        })
        .catch((err): void => {
          if (err.code === IdmPermissionDenied) {
            forceSignOffCallback(loginUser);
            return;
          } else if (err.code === 'Unauthorized') {
            if (callTlogInvalidPassword) {
              invalidLoginTlog(loginUser);
            }
            err.code = 'IdmInvalidUserNamePasswordLoginProvided';
          }
          setErrorMessage(err);
          resetUserLogin(true);
          if (err.code === SessionExpired) {
            unlockPos(true);
            setNormalLogin();
            setUserLockStatus(userLockStatus.None);
          }
        });
    },
    [
      setNormalLogin,
      forceSignOffCallback,
      setStartupState,
      setErrorMessage,
      callTlogInvalidPassword,
      useTrainingModeAPIs,
    ],
  );

  const forceSignInCallback = useCallback(
    async (supervisorLogin: ILoginUser): Promise<void> => {
      const loginToTraining = refUser.current.trainingMode;
      const credentials: ForceSignInDto = {
        user: refUser.current,
        supervisor: supervisorLogin,
        trainingMode: loginToTraining,
      };
      await forceSignIn(credentials, useTrainingModeAPIs)
        .then((): void => {
          setStartupState(XLRStartSequenceState.PostLogin);
        })
        .catch((err): void => {
          setErrorMessage(err);
          resetSupervisorLogin();
        });
    },
    [setStartupState, setErrorMessage, useTrainingModeAPIs],
  );

  const handleIAMLogin = async (iamUser: ILoginUser): Promise<void> => {
    await handleAllLogins(iamUser);
  };

  const handleLogin = async (event?: React.FormEvent<HTMLFormElement>): Promise<void> => {
    if (event) {
      event.preventDefault();
    }
    const loginUser: ILoginUser = lockUserStatus === userLockStatus.Supervisor ? supervisor : user;
    if (props.trainingMode) {
      loginUser.trainingMode = true;
      if (loginDate && loginTime) {
        loginUser.trainingModeEffectiveDatetime = `${loginDate} ${loginTime}`;
      } else if (loginDate) {
        loginUser.trainingModeEffectiveDatetime = `${loginDate} ${moment().format('HH:mm:ss')}`;
      } else if (loginTime) {
        loginUser.trainingModeEffectiveDatetime = `${moment().format(
          'YYYY-MM-DD',
        )} ${loginTime} UTC`;
      }
      if (loginUser.trainingModeEffectiveDatetime) {
        loginUser.trainingModeEffectiveDatetime = moment(loginUser.trainingModeEffectiveDatetime)
          .utc()
          .toISOString();
        store.dispatch(
          LoginActions.settrainingModeEffectiveDatetime(loginUser.trainingModeEffectiveDatetime),
        );
      }
    }
    await handleAllLogins(loginUser);
  };

  const handleAllLogins = useCallback(
    async (loginUser: ILoginUser, isScannedData?: boolean): Promise<void> => {
      // Info to get if in Edge IAM
      const loggedInUser = await getUser();
      const lockedUser = LocalStorage.getLockUser();
      const requiresForceSignOff = needToForceSignOff(loggedInUser, lockedUser);

      const { EDGE_IAM } = getMetaData().xlrConfig;
      if (!EDGE_IAM) {
        if (!isScannedData) {
          if (!loginUser.userName || !loginUser.password) {
            return;
          }
          if (errors.Date || errors.Time) {
            return;
          }
        }
      }
      if (lockedUser && EDGE_IAM && requiresForceSignOff) {
        const inTraining = loginUser.trainingMode;
        const inTransaction = store.getState().transaction.transactionIdExists;
        if (inTransaction) {
          setForceSignOffPopUp(true);
        } else {
          await forceSignOffCallback({ ...loginUser, trainingMode: inTraining });
        }
      } else if (refLockUserStatus.current === userLockStatus.Supervisor) {
        await forceSignInCallback(loginUser);
      } else if (refLockUserStatus.current === userLockStatus.RequireForceSignOff) {
        await forceSignOffCallback(loginUser);
      } else if (refUser.current.lockout) {
        await unlockPOS(loginUser);
      } else {
        await loginProcess(loginUser);
      }
    },
    [getUser, loginProcess, forceSignInCallback, forceSignOffCallback, unlockPOS, errors],
  );

  useEffect((): void => {
    const onDataScanned: ScannedDataHandler = async (scanData: string): Promise<void> => {
      const isScannedData = true;
      const loginUser: ILoginUser = {
        barcode: scanData,
        trainingMode: refUser.current.trainingMode,
      };
      if (refUser.current.password) {
        loginUser.password = refUser.current.password;
      }
      if (refLockUserStatus.current !== userLockStatus.Supervisor && !refUser.current.lockout) {
        setUser(loginUser);
      }
      await handleAllLogins(loginUser, isScannedData);
    };
    updateScannerCallback(onDataScanned);
  }, [handleAllLogins]);

  useFastLogin(loginProcess);

  const handleCancel = async (): Promise<void> => {
    if (user.lockout) {
      if (store.getState().transaction.transactionId) {
        setForceSignOffPopUp(true);
      } else {
        await handleForceSignOff();
      }
    } else {
      handleResetLogin();
    }
  };

  const handleForceSignOff = async (): Promise<void> => {
    const { EDGE_IAM } = getMetaData().xlrConfig;
    if (EDGE_IAM) {
      const inTraining = store.getState().user.trainingMode;
      await forceSignOffCallback({ trainingMode: inTraining });
    } else {
      await handleLogin();
    }
  };

  const handleResetLogin = (): void => {
    resetUserLogin();
    if (refLockUserStatus.current === userLockStatus.Supervisor) {
      setUserLockStatus(userLockStatus.None);
      setSubmitLabel('SignInView_SignIn');
      setErrors({});
    }
  };

  const resetUserLogin = (keepUserName?: boolean): void => {
    setUser(
      (prevUser: ILoginUser): ILoginUser => ({
        ...prevUser,
        userName: keepUserName ? prevUser.userName : '',
        password: '',
      }),
    );
  };

  const resetSupervisorLogin = (): void => {
    setSupervisor(
      (supervisor: ILoginUser): ILoginUser => ({
        ...supervisor,
        userName: '',
        password: '',
      }),
    );
  };

  const checkForLockScenario = (
    lockedUser: string,
    enteredUserName: string,
    setUserLockStatus: React.Dispatch<React.SetStateAction<userLockStatus>>,
    setForceSignOffBtnDisabled: React.Dispatch<React.SetStateAction<boolean>>,
    setUnlockBtnDisabled: React.Dispatch<React.SetStateAction<boolean>>,
  ): void => {
    const submittedUserName = enteredUserName ?? '';
    if (lockedUser.toUpperCase() !== submittedUserName.toUpperCase()) {
      setUserLockStatus(userLockStatus.RequireForceSignOff);
      setForceSignOffBtnDisabled(false);
      setUnlockBtnDisabled(true);
    } else {
      setUserLockStatus(userLockStatus.Locked);
      setForceSignOffBtnDisabled(true);
      setUnlockBtnDisabled(false);
    }
  };

  const showForceSignInPopup = (): JSX.Element => {
    return (
      <InteractiveDialogueContainer
        dialogTestIdPrefix='login'
        open={lockUserStatus === userLockStatus.ConfirmSupervisor}
        acceptButtonLabel={t('ForceSignInAuthorizationDetailsViewText')}
        handleClose={(): void => {
          setUserLockStatus(userLockStatus.None);
        }}
        handleAccept={(): void => {
          setSubmitLabel('Continue');
          setUserLockStatus(userLockStatus.Supervisor);
        }}
        component={
          <DynamicDialogue
            iconType={EmeraldDialogTypes.Warning}
            title={'ForceSignInHeader'}
            message={message ?? ''}
            dataTestIdPrefix='force-sign-in'
          ></DynamicDialogue>
        }
      />
    );
  };

  const showForceSignOffPopup = (): JSX.Element => {
    return (
      <InteractiveDialogueContainer
        dialogTestIdPrefix='login'
        open={forceSignOffPopUp}
        handleClose={(): void => {
          setForceSignOffPopUp(false);
        }}
        handleAccept={async (): Promise<void> => {
          setForceSignOffPopUp(false);
          await handleForceSignOff();
        }}
        component={<ForceSignoffDialogue />}
      />
    );
  };

  waitForDevices.current = false;

  useEffect((): void => {
    if (props.devicesAreDisconnected && props.showDevicePopup) {
      clearModals();
      showDeviceAlertDialog();
    }
    devicesReady.current = false;
  }, [props.devicesAreDisconnected, props.showDevicePopup, classes.deviceList]);

  const configuration = getCurrentXLRConfig();

  return (
    <>
      <UserLogin
        showTpId={false}
        showBuId={false}
        showPasswordLabel={configuration.showPasswordLabel}
        user={lockUserStatus === userLockStatus.Supervisor ? supervisor : user}
        setUser={lockUserStatus === userLockStatus.Supervisor ? setSupervisor : setUser}
        errors={errors}
        setErrors={setErrors}
        setLoginDate={setLoginDate}
        setLoginTime={setLoginTime}
        handleIAMLogin={handleIAMLogin}
        onSubmit={handleLogin}
        trainingMode={props.trainingMode}
        toggleWelcomeVisible={props.showMenu}
        lockStatus={lockUserStatus}
        lockLabel={lockLabel}
        alternateButton={{
          label: alternateBtnLabel,
          onClick: (): Promise<void> => handleCancel(),
          disabled: isAlternateButtonDisabled,
        }}
        submitButton={{
          label: submitLabel,
          onClick: (): void => {},
          disabled: isSubmitDisabled,
        }}
      />
      {showForceSignInPopup()}
      {showForceSignOffPopup()}
    </>
  );
};

const mapStateToProps = (state: AppState) => ({
  transactionExists: !!state.transaction.transactionIdExists,
  trainingMode: state.posMode.trainingMode,
  showDevicePopup: state.deviceNotification.showDevicePopup,
  devicesAreDisconnected: !!state.deviceStatus.devices.find(({ warning }) => warning),
});
const mapDispatchToProps = {
  showMenu: MenuPanelActions.toggle,
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginWrapper);
